import type { Account, AccountList } from "@eai-toolkit/client";
import { AccountApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { hydrateAccountsQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import {
  axiosInstance,
  fieldSortComparator,
  getAllResources,
  getSingleResourceWithPermissionLookup,
} from "./utils";

const accountApi = new AccountApi(undefined, undefined, axiosInstance);

export const accountsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllAccounts: builder.query<
      Account[],
      { limit?: number; permission?: string } | void
    >({
      queryFn: async (options) => {
        try {
          const permission = options?.permission;
          const { items } = await getAllResources<Account>(
            (params) =>
              accountApi.v1ClusterAccountGet({
                ...params,
                ...(permission && { permission: [permission] }),
              }),
            options === undefined ? undefined : { limit: options?.limit },
          );
          return {
            data: items.sort(fieldSortComparator("fullName")) as Account[],
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result, _error, options) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Account" as const, id })),
              {
                type: "Account",
                id: options?.permission ? `LIST_${options.permission}` : "LIST",
              },
            ]
          : [
              {
                type: "Account",
                id: options?.permission ? `LIST_${options.permission}` : "LIST",
              },
            ],
    }),

    getAccountById: builder.query<
      { account: Account; permissionStatus?: string },
      { id: string; permission?: string }
    >({
      queryFn: async ({ id: accountId, permission }) => {
        try {
          if (permission) {
            const result = await getSingleResourceWithPermissionLookup(
              () => accountApi.v1AccountGetById({ accountId }),
              permission,
            );
            if (result.error) {
              return {
                error: { status: "CUSTOM_ERROR", error: result.error },
              };
            }
            return {
              data: {
                account: result.data,
                permissionStatus: result.permissionLookupResult,
              },
            };
          }
          // No permission check requested, just fetch the account
          const response = await accountApi.v1AccountGetById({ accountId });
          return {
            data: {
              account: response.data as Account,
            },
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: "Account", id }],
    }),

    getAccounts: builder.query<AccountList, { queryKey: string }>({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateAccountsQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(sort.fieldName, sort.direction);

          const response = await accountApi.v1ClusterAccountGet({
            offset,
            limit,
            q: filtersToQ(filters),
            order,
          });

          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.items ?? []).map(({ id }) => ({
                type: "Account" as const,
                id,
              })),
              { type: "Account", id: "SEARCH_LIST" },
            ]
          : [{ type: "Account", id: "SEARCH_LIST" }],
    }),
  }),
});

export const {
  useGetAllAccountsQuery,
  useGetAccountByIdQuery,
  useGetAccountsQuery,
} = accountsApi;
